/* menu-item.tsx generated by @compiled/babel-plugin v0.29.1 */
import _extends from "@babel/runtime/helpers/extends";
import "./menu-item.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
/**
 * @jsxFrag
 */
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { cx } from '@compiled/react';
import { AvatarContext } from '@atlaskit/avatar';
import { Anchor, Pressable, Text } from '@atlaskit/primitives';
import { useLevel } from './expandable-menu-item-context';
import { useFlyoutMenuOpen, useSetFlyoutMenuOpen } from './flyout-menu-item-context';
import { COLLAPSE_ELEM_BEFORE } from './menu-item-signals';
import { MenuListItem } from './menu-list-item';
var defaultAvatarValues = {
  size: 'small'
};
var hoverActionsDisplayVar = '--hover-actions-display';
var elemAfterDisplayVar = '--elem-after-display';
var notchColorVar = '--notch-color';

/**
 * ## 🤹 `position:relative`
 *
 * We need `position:relative` on an element that takes
 * up the full size of the interactive element so we
 * can correctly use `position:absolute` to place:
 * 1. the notch for links
 * 2. a child of button / anchor to stretch it out to
 *    increase it's pressable area.
 *
 * ⛔️ We cannot add `position:relative` _only_ on the
 * button / anchor as that will cause sibling elements
 * to be rendered under the button / anchor when setting
 * a background color on the button / anchor.
 *
 * 📖 Note: `position:relative` elements are painted after
 * elements with `position:static` (the default)
 * https://drafts.csswg.org/css-position-4/#painting-order
 *
 * ⛔️ We cannot add `position:relative` to the container
 * element, as then the `:focus` ring styles on the
 * button / anchor can be cut off by the next sibling if it has
 * has a background color set (eg when selected)
 *
 * ✅ Add `position:relative` to all first level descendants
 * of the container element so that we don't impact DOM ordered
 * paint ordering within the item and the button / anchor focus
 * ring can still bleed over siblings
 *
 * 📖 We could use `> * { position: relative; }` on the container,
 * but that would violate our styling standard.
 */
var topLevelSiblingStyles = {
  root: "_kqswh2mm"
};

/**
 * All slots on the menu item (eg `elemBefore`) are rendered as siblings
 * of our main button / anchor element and they are visually placed on
 * top of the main button / anchor.
 *
 * 📖 This is done so that we don't nest interactive elements in our markup.
 *
 * ✅ This is great when element in the slot is an interactive element
 * as we don't want the main menu item button / anchor to be triggered
 * when interacting with the element in the slot.
 *
 * ⛔️ When the element in the slot is static content (eg an `<Icon>`) it will
 * prevent the main button / anchor (that is visually behind the element in
 * the slot) from being clicked. The element in the slot is a sibling of our
 * main button / anchor (not a child of it) so clicking on the element in the
 * slot will not bubble up to the button / anchor.
 *
 * 🚀 We set `pointer-events:none` on a slot if it does not contain and interactive
 * element so that static content in a slot does not prevent clicking on the main
 * button / anchor.
 */
var onTopOfButtonOrAnchorStyles = {
  root: "_18ukglyw"
};
var containerStyles = {
  root: "_vchhusvi _1e0c11p5 _yv0eaa4y _4cvr1h6o _4t3izwfg _uiztglyw _2rkot9bt _syaz1gjq _ok65glyw _1yyu1j28 _rkdi1ji5 _19w515ej _19lc108i _u31n15ej _1t6i1txw _140e1txw",
  removeElemAfterOnHover: "_91ju1txw _1mfcglyw _1sjuglyw",
  showHoverActions: "_ok651txw _91juglyw",
  selected: "_11q7fg4m _syazaqb7 _1yyu1fvw _30l3aqb7 _19lci1yw _u31n1fvw",
  pressed: "_11q71ji5 _1yyu15ej",
  hasDescription: "_4t3i1ady"
};
var buttonOrAnchorStyles = {
  root: "_1e0c11p5 _yyhyjvu9 _1ii7kb7n _yv0enbh3 _4t3i1osq _1yt41b66 _11q7glyw _2rkot9bt _syaz1gjq _4cvr1h6o _y3gnv2br _j6xt1ji5",
  selected: "_syazaqb7 _j6xt19ru"
};
var extendButtonOrAnchorStyles = {
  root: "_kqswstnw _1r04ze3t"
};
var notchStyles = {
  root: "_kqswstnw _152t1ssb _1e02ze3t _1bsbyh40 _4t3i1crf _t9ec1i34 _bfhk1o0g"
};
var actionStyles = {
  root: "_1e0c1txw _4cvr1h6o _zulp1b66 _1ii7kb7n _yyhyqfx8 _y4ti1b66"
};
var actionOnHoverStyles = {
  root: "_1e0c1obi _1ii7kb7n _yyhy11wp _4cvr1h6o _zulp1b66 _y4ti1b66"
};
var textStyles = {
  root: "_yyhycs5v _y4ti1b66 _bozg1b66",
  noElemBeforeIndent: "_bozg12x7"
};
var elemBeforeStyles = {
  root: "_yyhykb7n _1ii7kb7n _1e0c1txw _1o9zidpf _1bsb1tcg _4t3i1tcg _4cvr1h6o _1bah1h6o _bozg1b66"
};
var elemAfterStyles = {
  root: "_1e0cgmyj _yyhyegat _1ii7kb7n _1o9zidpf _4t3i1tcg _4cvr1h6o _y4ti1b66"
};
/**
 * __MenuItemBase__
 *
 * The base menu item component used to compose MenuButtonItem and MenuLinkItem.
 */
export var MenuItemBase = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var testId = _ref.testId,
    actions = _ref.actions,
    actionsOnHover = _ref.actionsOnHover,
    children = _ref.children,
    description = _ref.description,
    elemAfter = _ref.elemAfter,
    elemBefore = _ref.elemBefore,
    href = _ref.href,
    target = _ref.target,
    isDisabled = _ref.isDisabled,
    isSelected = _ref.isSelected,
    onClick = _ref.onClick,
    ariaControls = _ref.ariaControls,
    ariaExpanded = _ref.ariaExpanded,
    ariaHasPopup = _ref.ariaHasPopup,
    interactionName = _ref.interactionName;
  var level = useLevel();
  var setFlyoutMenuOpen = useSetFlyoutMenuOpen();
  var isFlyoutMenuOpen = useFlyoutMenuOpen();
  var isLink = typeof href !== 'undefined';
  var handleClick = useCallback(function (event, analyticsEvent) {
    onClick === null || onClick === void 0 || onClick(event, analyticsEvent);
    // Toggle flyout menu open state when inside a flyout context provider
    setFlyoutMenuOpen(!isFlyoutMenuOpen);
  }, [onClick, setFlyoutMenuOpen, isFlyoutMenuOpen]);
  var buttonAndAnchorSharedProps = {
    'aria-controls': ariaControls,
    'aria-haspopup': ariaHasPopup,
    onClick: handleClick
  };

  // By default provide the spacing for `elemBefore` to have good
  // vertical alignment of labels and to give clear indentation between levels
  // in the side navigation (even when items don't use elemBefore).
  var showElemBefore = elemBefore !== COLLAPSE_ELEM_BEFORE;
  var interactiveElemContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    // This extends the clickable area of nested menu items to the width
    // of the root level menu items, while being visually indented.
    style: {
      insetInlineStart: "-".concat(level * 26, "px")
    },
    "aria-hidden": "true",
    className: ax([extendButtonOrAnchorStyles.root])
  }), /*#__PURE__*/React.createElement("div", {
    className: ax([textStyles.root, !showElemBefore && textStyles.noElemBeforeIndent])
  }, /*#__PURE__*/React.createElement(Text, {
    weight: "medium",
    maxLines: 1,
    color: isSelected ? 'color.text.selected' : 'color.text.subtle'
  }, children), description && /*#__PURE__*/React.createElement(Text, {
    color: "color.text.subtlest",
    size: "small",
    maxLines: 1
  }, description)));
  return /*#__PURE__*/React.createElement(AvatarContext.Provider, {
    value: defaultAvatarValues
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": testId ? "".concat(testId, "-container") : undefined,
    "data-selected": isSelected,
    className: ax([containerStyles.root, isSelected && containerStyles.selected, description && containerStyles.hasDescription, ariaHasPopup && ariaExpanded && containerStyles.pressed, actionsOnHover && elemAfter && containerStyles.removeElemAfterOnHover, !ariaHasPopup && ariaExpanded && actionsOnHover && containerStyles.showHoverActions])
  }, isLink ? /*#__PURE__*/React.createElement(Anchor, _extends({}, buttonAndAnchorSharedProps, {
    xcss: cx(buttonOrAnchorStyles.root, topLevelSiblingStyles.root, isSelected && buttonOrAnchorStyles.selected)
    // Needed to override Anchor style due to a compiled/emotion conflict
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
    ,
    style: {
      textDecoration: 'none'
    },
    "aria-current": isSelected ? 'page' : undefined,
    href: href,
    target: target,
    ref: forwardedRef,
    testId: testId,
    interactionName: interactionName
  }), /*#__PURE__*/React.createElement("div", {
    "aria-hidden": "true",
    className: ax([notchStyles.root])
  }), interactiveElemContent) : /*#__PURE__*/React.createElement(Pressable, _extends({}, buttonAndAnchorSharedProps, {
    xcss: cx(buttonOrAnchorStyles.root, topLevelSiblingStyles.root, isSelected && buttonOrAnchorStyles.selected),
    "aria-expanded": ariaExpanded,
    isDisabled: isDisabled,
    ref: forwardedRef,
    testId: testId,
    interactionName: interactionName
  }), interactiveElemContent), showElemBefore && /*#__PURE__*/React.createElement("div", {
    className: ax([elemBeforeStyles.root, topLevelSiblingStyles.root, onTopOfButtonOrAnchorStyles.root])
  }, elemBefore), actionsOnHover && /*#__PURE__*/React.createElement("div", {
    className: ax([actionOnHoverStyles.root, topLevelSiblingStyles.root, onTopOfButtonOrAnchorStyles.root])
  }, actionsOnHover), elemAfter && /*#__PURE__*/React.createElement("div", {
    className: ax([elemAfterStyles.root, topLevelSiblingStyles.root, onTopOfButtonOrAnchorStyles.root])
  }, elemAfter), actions && /*#__PURE__*/React.createElement("div", {
    className: ax([actionStyles.root, topLevelSiblingStyles.root, onTopOfButtonOrAnchorStyles.root])
  }, actions)));
});

/**
 * __MenuButtonItem__
 *
 * A menu item button. It should be used within a `ul`.
 */
export var MenuButtonItem = /*#__PURE__*/forwardRef(function (_ref2, forwardedRef) {
  var testId = _ref2.testId,
    actions = _ref2.actions,
    children = _ref2.children,
    description = _ref2.description,
    elemAfter = _ref2.elemAfter,
    isDisabled = _ref2.isDisabled,
    elemBefore = _ref2.elemBefore,
    actionsOnHover = _ref2.actionsOnHover,
    onClick = _ref2.onClick,
    interactionName = _ref2.interactionName;
  return /*#__PURE__*/React.createElement(MenuListItem, null, /*#__PURE__*/React.createElement(MenuItemBase, {
    testId: testId,
    description: description,
    actions: actions,
    elemAfter: elemAfter,
    elemBefore: elemBefore,
    isDisabled: isDisabled,
    actionsOnHover: actionsOnHover,
    onClick: onClick,
    ref: forwardedRef,
    interactionName: interactionName
  }, children));
});
var MenuLinkItemNoRef = function MenuLinkItemNoRef(_ref3, forwardedRef) {
  var testId = _ref3.testId,
    actions = _ref3.actions,
    children = _ref3.children,
    description = _ref3.description,
    elemAfter = _ref3.elemAfter,
    elemBefore = _ref3.elemBefore,
    href = _ref3.href,
    target = _ref3.target,
    actionsOnHover = _ref3.actionsOnHover,
    isSelected = _ref3.isSelected,
    onClick = _ref3.onClick,
    interactionName = _ref3.interactionName;
  var itemRef = useRef(null);
  useEffect(function () {
    var _itemRef$current;
    // JSDOM does not support scrollIntoView, so ensuring it exists first (see https://github.com/jsdom/jsdom/issues/1695)
    if (isSelected && (_itemRef$current = itemRef.current) !== null && _itemRef$current !== void 0 && _itemRef$current.scrollIntoView) {
      itemRef.current.scrollIntoView({
        block: 'nearest'
      });
    }
    // This effect should only run on initial mount, otherwise the sidebar would scroll every time an item is selected
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement(MenuListItem, {
    ref: itemRef
  }, /*#__PURE__*/React.createElement(MenuItemBase, {
    testId: testId,
    description: description,
    actions: actions,
    elemAfter: elemAfter
    // TODO Always use "open in new window" icon when `openInNewWindow` prop is true
    ,
    elemBefore: elemBefore,
    href: href,
    target: target,
    actionsOnHover: actionsOnHover,
    isSelected: isSelected,
    onClick: onClick,
    ref: forwardedRef,
    interactionName: interactionName
  }, children));
};

// Workaround to support generic types with forwardRef
/**
 * __MenuLinkItem__
 *
 * A menu item link. It should be used within a `ul`.
 */
export var MenuLinkItem = /*#__PURE__*/forwardRef(MenuLinkItemNoRef);